import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Devices from "@material-ui/icons/Devices";
import Assignment from "@material-ui/icons/Assignment";
import Business from "@material-ui/icons/Business";
import SupervisorAccount from "@material-ui/icons/SupervisorAccount";
import Storage from "@material-ui/icons/Storage";
import BarChart from "@material-ui/icons/BarChart";
import { colors, fontFamily, maxWidth } from "./tokens";
import Reveal from "./Reveal";

const useStyles = makeStyles(theme => ({
  section: {
    padding: theme.spacing(12, 3),
    background: colors.surfaceMuted,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(8, 2.5)
    }
  },
  inner: {
    maxWidth,
    margin: "0 auto"
  },
  headline: {
    fontFamily,
    fontWeight: 700,
    letterSpacing: "-0.03em",
    fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
    color: colors.ink,
    textAlign: "center",
    margin: 0,
    marginBottom: theme.spacing(7)
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "repeat(2, 1fr)"
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr"
    }
  },
  card: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 14,
    padding: theme.spacing(3.5, 3),
    transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
    "&:hover": {
      transform: "translateY(-3px)",
      borderColor: colors.inkFaint,
      boxShadow: "0 16px 32px -24px rgba(18,21,26,0.25)"
    }
  },
  iconChip: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 38,
    height: 38,
    borderRadius: 10,
    background: colors.primarySoft,
    marginBottom: theme.spacing(2)
  },
  icon: {
    fontSize: 19,
    color: colors.primaryDark
  },
  title: {
    fontFamily,
    fontWeight: 600,
    fontSize: 16,
    color: colors.ink,
    marginBottom: theme.spacing(1)
  },
  text: {
    fontFamily,
    fontSize: 13.5,
    lineHeight: 1.6,
    color: colors.inkMuted,
    margin: 0
  }
}));

const features = [
  {
    Icon: Devices,
    title: "Assets & Sites",
    text: "Know what you own, where it is, who's responsible, its warranty/coverage status and maintenance schedule, and its full history, with QR-based lookup for instant access on site."
  },
  {
    Icon: Assignment,
    title: "Service Requests & Work Orders",
    text: "Route issues to the right team and manage work from request to completion."
  },
  {
    Icon: SupervisorAccount,
    title: "Maintenance Teams & Providers",
    text: "Coordinate internal technicians and external providers in one workflow."
  },
  {
    Icon: Storage,
    title: "Inventory & Procurement",
    text: "Track warehouse stock, material requests and purchase orders together."
  },
  {
    Icon: BarChart,
    title: "Reporting & Insights",
    text: "From maintenance performance to asset valuation, depreciation and executive summaries, get the reports that keep operations and finance aligned."
  },
  {
    Icon: Business,
    title: "Organisation & Access",
    text: "Manage your users, roles and permissions as your team grows."
  }
];

export default function Capabilities() {
  const classes = useStyles();

  return (
    <section id="capabilities" className={classes.section}>
      <div className={classes.inner}>
        <Reveal as="h2" className={classes.headline}>
          Key capabilities.
        </Reveal>

        <div className={classes.grid}>
          {features.map(({ Icon, title, text }, i) => (
            <Reveal as="div" className={classes.card} delay={i * 60} key={title}>
              <span className={classes.iconChip}>
                <Icon className={classes.icon} />
              </span>
              <div className={classes.title}>{title}</div>
              <p className={classes.text}>{text}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
